import type { ReactNode } from 'react'

/**
 * The panel with a notch cut out of its top centre.
 *
 * Used by the auth screens (design-spec.md §5.9) and again by the wallet,
 * portfolio and dashboard, where the same 100-wide cut sits at the top of the
 * content sheet. The mask geometry — a 100×16.3 cut with 8px bottom corners
 * and 6px concave fillets, so a 112-wide punch — is measured off
 * renders/03-login.png; no render exists for the wallet-side panels, so they
 * reuse it rather than trusting the node tree's 15px depth.
 *
 * A 72×8 pill sits inside the notch. On the auth screens it reads as a dark
 * pill against the blue hero; on the wallet screens the design gives it the
 * page background, where it is all but invisible. Both are drawn as specified.
 *
 * The mask and the pill are absolutely positioned, and positioned elements
 * paint above static in-flow content — so without `isolate` plus a negative
 * z-index they cover the children, and every caller has to remember its own
 * `relative`. Containing the stacking context here means they do not: a panel
 * whose caller forgets it renders the same as one that does not.
 */
interface NotchedPanelProps {
  children?: ReactNode
  /** Colour of the pill inside the notch. */
  pillClassName?: string
  className?: string
}

export function NotchedPanel({
  children,
  pillClassName = 'bg-bg',
  className = '',
}: NotchedPanelProps) {
  return (
    <div className={`relative isolate ${className}`}>
      <div
        className="sx-notch-panel absolute inset-0 -z-10 rounded-t-20 bg-bg"
        aria-hidden="true"
      />
      <div
        className={`absolute start-1/2 top-0 -z-10 h-[8px] w-[72px] -translate-x-1/2 rounded-6 rtl:translate-x-1/2 ${pillClassName}`}
        aria-hidden="true"
      />
      {children}
    </div>
  )
}
